import type { FC, ReactNode } from 'react';
import { Tag } from '../../../core-components/Tag';
import { Icon, IconName } from '../../../core-components/Icon';
import { twJoin } from '@tuic/tailwind-config';
import {
  Button,
  ButtonProps,
  Skeleton,
  SkeletonTextLines,
  TextButton,
  TextButtonProps,
} from '../../../core-components';
import { DataRow, RowItem } from '../DataRow';

export type EventTimelineEntryProps = {
  fallback?: boolean;
  topline?: string;
  title?: ReactNode | { from: ReactNode; to: ReactNode };
  warningTag?: string;
  description?: ReactNode;
  mainCta?: ButtonProps;
  secondaryCta?: TextButtonProps;
  rowItems?: RowItem[];
  status?: 'upcoming' | 'finished' | 'warning';
  startIcon?: IconName;
  endIcon?: IconName;
};

export const EventTimelineEntry: FC<EventTimelineEntryProps> = ({
  fallback,
  title,
  topline,
  status = 'upcoming',
  rowItems,
  description,
  mainCta,
  secondaryCta,
  startIcon,
  endIcon,
  warningTag,
}) => {
  return (
    <div className="flex w-full flex-row text-marine-high-emphasis">
      <div className="mr-4 flex flex-col items-center">
        {(startIcon || fallback) && (
          <Icon
            name={startIcon ?? 'circle'}
            size="md"
            className={twJoin(status === 'warning' && 'text-attention-120')}
          />
        )}
        <div className="flex h-full w-full justify-center ">
          <div
            className={twJoin(
              'my-1 border-l rounded-full',
              status === 'upcoming' && 'border-stroke-secondary-40',
              status === 'finished' && 'border-stroke-secondary-100',
              status === 'warning' && 'border-stroke-attention-120',
            )}
          />
        </div>
        {endIcon && (
          <Icon
            name={endIcon}
            size="md"
            className={twJoin(status === 'warning' && 'text-attention-120')}
          />
        )}
      </div>
      <div className="flex flex-grow flex-col gap-4 pb-9">
        {warningTag && <Tag variant="attention">{warningTag}</Tag>}
        <div>
          {topline && (
            <div className="text-marine-medium-emphasis">{topline}</div>
          )}
          <div
            className={twJoin(
              'headline-sm',
              typeof title === 'object' &&
                'flex flex-col md:flex-row md:items-center gap-1',
            )}
          >
            {fallback ? (
              <SkeletonTextLines number={1} className="w-1/3" />
            ) : typeof title === 'object' &&
              title !== null &&
              'from' in title &&
              'to' in title ? (
              <>
                <span>{title.from}</span>
                <Icon name="arrow-right" size="xs" />
                <span>{title.to}</span>
              </>
            ) : (
              title
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            {fallback ? (
              <SkeletonTextLines number={2} />
            ) : (
              <div className="text-lg line-clamp-2">{description}</div>
            )}

            {secondaryCta && <TextButton {...secondaryCta} />}
          </div>

          {rowItems && (
            <ul>
              {rowItems.map((rowItem, index) => (
                <DataRow key={index} {...rowItem} />
              ))}
            </ul>
          )}
        </div>

        {fallback ? (
          <Button fallback />
        ) : (
          mainCta && (
            <div>
              <Button
                {...mainCta}
                variant="secondary"
                className="w-full md:w-auto"
              />
            </div>
          )
        )}
      </div>
    </div>
  );
};
